import * as THREE from "three";
import { TransformControls as TransformControlsImpl } from "three-stdlib";
import { SymbolType } from "../../../types/floorplannerTypes";

export type Symbol3DRotation = NonNullable<SymbolType["threeD"]>["rotation3D"];

export const MIN_ROTATION_GIZMO_PARENT_SCALE = 1e-4;

const MODEL_ROTATION_EPSILON = 1e-8;
const TRANSFORM_CONTROLS_INTERNAL_RAYCAST_LAYER = 31;
const ROTATION_GIZMO_FOOTPRINT_RATIO = 0.72;
const MIN_ROTATION_GIZMO_WORLD_DIAMETER = 0.42;
const MAX_ROTATION_GIZMO_WORLD_DIAMETER = 1.05;
const ROTATION_GIZMO_OUTER_RING_RADIUS = 1.25;
const TRANSFORM_CONTROLS_SIZE_DENOMINATOR = 7;
const MIN_ROTATION_GIZMO_CONTROL_SIZE = 0.015;
const MAX_ROTATION_GIZMO_CONTROL_SIZE = 0.85;
const DEFAULT_TRANSFORM_CONTROLS_CAMERA_FACTOR = 7;
const ROTATION_GIZMO_AXIS_OVERLAY_RADIUS = 0.02;
const ROTATION_GIZMO_AXIS_OVERLAY_SEGMENTS = 64;
const registeredTransformControls = new Set<TransformControlsImpl>();
const ROTATION_AXIS_NAMES = ["X", "Y", "Z", "E"] as const;
export const ROTATION_GIZMO_SNAP_STEP = Math.PI / 4;
/** Amplifies TransformControls rotate drag so smaller mouse moves rotate more. */
export const ROTATION_GIZMO_SENSITIVITY_MULTIPLIER = 2.5;

export type RotationGizmoSensitivitySession = {
  startQuaternion: THREE.Quaternion;
};

/**
 * Captures the object's quaternion at drag start so TransformControls deltas can
 * be re-scaled in {@link applyRotationGizmoSensitivity}.
 */
export function createRotationGizmoSensitivitySession(
  object: THREE.Object3D,
): RotationGizmoSensitivitySession {
  return {
    startQuaternion: object.quaternion.clone(),
  };
}

/**
 * Re-applies the drag delta from TransformControls with higher gain.
 * Skips amplification when snapping is active so Shift-snap stays exact.
 */
export function applyRotationGizmoSensitivity(
  object: THREE.Object3D,
  session: RotationGizmoSensitivitySession | null | undefined,
  options: {
    multiplier?: number;
    snapStep?: number | null;
  } = {},
): void {
  if (!session) return;

  const snapStep = options.snapStep;
  if (snapStep && snapStep > 0) return;

  const multiplier = options.multiplier ?? ROTATION_GIZMO_SENSITIVITY_MULTIPLIER;
  if (!Number.isFinite(multiplier) || multiplier <= 1) return;

  const delta = session.startQuaternion
    .clone()
    .invert()
    .multiply(object.quaternion)
    .normalize();
  const angle = 2 * Math.acos(THREE.MathUtils.clamp(delta.w, -1, 1));
  if (angle < MODEL_ROTATION_EPSILON) return;

  const axis = new THREE.Vector3(delta.x, delta.y, delta.z);
  if (axis.lengthSq() < MODEL_ROTATION_EPSILON) return;
  axis.normalize();

  const scaledDelta = new THREE.Quaternion().setFromAxisAngle(
    axis,
    angle * multiplier,
  );
  object.quaternion
    .copy(session.startQuaternion)
    .multiply(scaledDelta)
    .normalize();
}

type RotationGizmoControlSizeOptions = {
  camera?: THREE.Camera;
  worldPosition?: THREE.Vector3;
  parentScale?: number;
};

export function getSymbol3DRotationFromObject(
  object: THREE.Object3D,
): Symbol3DRotation {
  return getSymbol3DRotationFromQuaternion(object.quaternion);
}

export function getSymbol3DRotationFromQuaternion(
  quaternion: THREE.Quaternion,
): Symbol3DRotation {
  const euler = new THREE.Euler().setFromQuaternion(quaternion, "XYZ");
  return {
    pitch: normalizeModelRotation(euler.x),
    roll: normalizeModelRotation(euler.y),
    yaw: normalizeModelRotation(euler.z),
  };
}

export function compactSymbol3DRotation(
  rotation3D: Symbol3DRotation,
): Symbol3DRotation | undefined {
  if (!rotation3D) return undefined;
  const compact: NonNullable<Symbol3DRotation> = {};
  if (Math.abs(rotation3D.pitch ?? 0) > MODEL_ROTATION_EPSILON) {
    compact.pitch = rotation3D.pitch;
  }
  if (Math.abs(rotation3D.roll ?? 0) > MODEL_ROTATION_EPSILON) {
    compact.roll = rotation3D.roll;
  }
  if (Math.abs(rotation3D.yaw ?? 0) > MODEL_ROTATION_EPSILON) {
    compact.yaw = rotation3D.yaw;
  }
  return Object.keys(compact).length > 0 ? compact : undefined;
}

/**
 * Keeps the 2D plan rotation aligned when 3D yaw changes (blue axis).
 * Pitch and roll stay in rotation3D; yaw is folded into symbol.rotation.
 */
export function syncSymbolPlanRotationAfter3DYawChange(
  planRotation: number | undefined,
  previousRotation3D: Symbol3DRotation | undefined,
  nextRotation3D: Symbol3DRotation | undefined,
): { rotation: number; rotation3D: Symbol3DRotation | undefined } {
  const previousYaw = previousRotation3D?.yaw ?? 0;
  const nextYaw = nextRotation3D?.yaw ?? 0;
  const yawDelta = nextYaw - previousYaw;

  if (Math.abs(yawDelta) < MODEL_ROTATION_EPSILON) {
    return {
      rotation: planRotation ?? 0,
      rotation3D: compactSymbol3DRotation(nextRotation3D),
    };
  }

  return {
    rotation: (planRotation ?? 0) + nextYaw,
    rotation3D: compactSymbol3DRotation({
      ...nextRotation3D,
      yaw: 0,
    }),
  };
}

export function getRotationGizmoControlSize(
  dimensions: {
    width?: number;
    depth?: number;
    height?: number;
  },
  optionsOrParentScale: RotationGizmoControlSizeOptions | number = {},
): number {
  const options =
    typeof optionsOrParentScale === "number"
      ? { parentScale: optionsOrParentScale }
      : optionsOrParentScale;
  const cameraFactor = getTransformControlsCameraFactor(
    options.camera,
    options.worldPosition,
  );
  const targetHandleScale =
    getRotationGizmoWorldSize(dimensions) /
    (ROTATION_GIZMO_OUTER_RING_RADIUS * 2);
  const parentScale = Math.max(
    options.parentScale ?? 1,
    MIN_ROTATION_GIZMO_PARENT_SCALE,
  );
  return THREE.MathUtils.clamp(
    (targetHandleScale * TRANSFORM_CONTROLS_SIZE_DENOMINATOR) /
      cameraFactor /
      parentScale,
    MIN_ROTATION_GIZMO_CONTROL_SIZE,
    MAX_ROTATION_GIZMO_CONTROL_SIZE,
  );
}

export function getRotationGizmoWorldSize(dimensions: {
  width?: number;
  depth?: number;
  height?: number;
}): number {
  const footprintDimensions = [dimensions.width, dimensions.depth].filter(
    isPositiveFiniteNumber,
  );
  const footprint =
    footprintDimensions.length >= 2
      ? Math.min(...footprintDimensions)
      : (footprintDimensions[0] ?? dimensions.height);
  const targetSize =
    (isPositiveFiniteNumber(footprint)
      ? footprint
      : MIN_ROTATION_GIZMO_WORLD_DIAMETER) * ROTATION_GIZMO_FOOTPRINT_RATIO;

  return THREE.MathUtils.clamp(
    targetSize,
    MIN_ROTATION_GIZMO_WORLD_DIAMETER,
    MAX_ROTATION_GIZMO_WORLD_DIAMETER,
  );
}

export function configureTransformControlsInternalRaycastLayer(
  controls: TransformControlsImpl,
) {
  const internals = controls as unknown as TransformControlsRaycastInternals;
  internals.raycaster?.layers.set(TRANSFORM_CONTROLS_INTERNAL_RAYCAST_LAYER);
  internals.plane?.layers.set(TRANSFORM_CONTROLS_INTERNAL_RAYCAST_LAYER);
  Object.values(internals.gizmo?.picker ?? {}).forEach((picker) => {
    picker?.traverse((child) =>
      child.layers.set(TRANSFORM_CONTROLS_INTERNAL_RAYCAST_LAYER),
    );
  });
}

export function registerTransformControlsPointerGuard(
  controls: TransformControlsImpl,
): () => void {
  configureTransformControlsInternalRaycastLayer(controls);
  enhanceRotationGizmoVisibility(controls);
  registeredTransformControls.add(controls);
  return () => {
    registeredTransformControls.delete(controls);
  };
}

function enhanceRotationGizmoVisibility(controls: TransformControlsImpl) {
  const gizmoRoot = getTransformControlsGizmoRoot(controls);
  const rotateGizmo = gizmoRoot?.gizmo?.rotate;
  if (!rotateGizmo) return;

  rotateGizmo.children.forEach((child) => {
    if (!(child instanceof THREE.Line)) return;
    addRotationAxisOverlay(child);
  });
}

function addRotationAxisOverlay(line: THREE.Line) {
  if (!isRotationAxisName(line.name)) return;
  if (line.getObjectByName(`${line.name}-overlay`)) return;

  const overlayGeometry = createAxisOverlayGeometry(line);
  if (!overlayGeometry) return;

  const overlayMaterial = createOverlayMaterial(line);
  const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
  overlay.name = `${line.name}-overlay`;
  overlay.renderOrder = line.renderOrder;
  line.add(overlay);
}

function createAxisOverlayGeometry(line: THREE.Line): THREE.BufferGeometry | null {
  const positions = line.geometry.attributes.position;
  if (!positions) return null;

  const points: THREE.Vector3[] = [];
  for (let index = 0; index < positions.count; index += 1) {
    const vertex = new THREE.Vector3(
      positions.getX(index),
      positions.getY(index),
      positions.getZ(index),
    );
    points.push(vertex);
  }

  if (points.length < 2) return null;
  const curve = new THREE.CatmullRomCurve3(points);
  return new THREE.TubeGeometry(
    curve,
    Math.max(ROTATION_GIZMO_AXIS_OVERLAY_SEGMENTS, points.length * 2),
    ROTATION_GIZMO_AXIS_OVERLAY_RADIUS,
    6,
    false,
  );
}

function createOverlayMaterial(line: THREE.Line): THREE.MeshBasicMaterial {
  const baseMaterial = Array.isArray(line.material)
    ? line.material[0]
    : line.material;
  const color =
    baseMaterial instanceof THREE.Material && "color" in baseMaterial
      ? (baseMaterial as THREE.LineBasicMaterial).color.getHex()
      : 0xffffff;

  const overlay = new THREE.MeshBasicMaterial({
    color,
    toneMapped: false,
    transparent: true,
  });
  overlay.depthWrite = false;
  overlay.depthTest = false;
  overlay.opacity =
    baseMaterial instanceof THREE.LineBasicMaterial
      ? baseMaterial.opacity
      : 1;
  return overlay;
}

export function isPointerOverRegisteredTransformControlsPicker(event: {
  button?: number;
  clientX: number;
  clientY: number;
}): boolean {
  for (const controls of registeredTransformControls) {
    if (isPointerOverTransformControlsPicker(controls, event)) {
      return true;
    }
  }
  return false;
}

export function isPointerOverTransformControlsPicker(
  controls: TransformControlsImpl | null | undefined,
  event: {
    button?: number;
    clientX: number;
    clientY: number;
  },
): boolean {
  if (!controls) return false;

  const internals = controls as unknown as TransformControlsRaycastInternals;
  if (
    internals.enabled === false ||
    !internals.visible ||
    !internals.object ||
    !internals.camera ||
    !internals.domElement
  ) {
    return false;
  }

  const picker = internals.gizmo?.picker?.[internals.mode ?? "rotate"];
  if (!picker) return false;

  const rect = internals.domElement.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;

  configureTransformControlsInternalRaycastLayer(controls);
  controls.updateMatrixWorld();
  picker.updateMatrixWorld(true);

  const pointer = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
  const raycaster = internals.raycaster ?? new THREE.Raycaster();
  raycaster.layers.set(TRANSFORM_CONTROLS_INTERNAL_RAYCAST_LAYER);
  raycaster.setFromCamera(pointer, internals.camera);

  return raycaster
    .intersectObject(picker, true)
    .some((intersection) => intersection.object.visible);
}

export function positiveDimension(
  value: number | undefined,
): number | undefined {
  return isPositiveFiniteNumber(value) ? value : undefined;
}

function normalizeModelRotation(value: number): number {
  if (!Number.isFinite(value) || Math.abs(value) < MODEL_ROTATION_EPSILON) {
    return 0;
  }
  return value;
}

function getTransformControlsCameraFactor(
  camera: THREE.Camera | undefined,
  worldPosition: THREE.Vector3 | undefined,
): number {
  if (!camera || !worldPosition)
    return DEFAULT_TRANSFORM_CONTROLS_CAMERA_FACTOR;

  if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
    const orthographicCamera = camera as THREE.OrthographicCamera;
    const zoom = positiveDimension(orthographicCamera.zoom) ?? 1;
    const factor = (orthographicCamera.top - orthographicCamera.bottom) / zoom;
    return (
      positiveDimension(factor) ?? DEFAULT_TRANSFORM_CONTROLS_CAMERA_FACTOR
    );
  }

  if ((camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
    const perspectiveCamera = camera as THREE.PerspectiveCamera;
    const zoom = positiveDimension(perspectiveCamera.zoom) ?? 1;
    const fov = positiveDimension(perspectiveCamera.fov) ?? 50;
    const cameraPosition = perspectiveCamera.getWorldPosition(
      new THREE.Vector3(),
    );
    const distance = worldPosition.distanceTo(cameraPosition);
    if (!isPositiveFiniteNumber(distance)) {
      return DEFAULT_TRANSFORM_CONTROLS_CAMERA_FACTOR;
    }
    return (
      distance *
      Math.min((1.9 * Math.tan(THREE.MathUtils.degToRad(fov) / 2)) / zoom, 7)
    );
  }

  return DEFAULT_TRANSFORM_CONTROLS_CAMERA_FACTOR;
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function isRotationAxisName(name: string) {
  return ROTATION_AXIS_NAMES.includes(name as typeof ROTATION_AXIS_NAMES[number]);
}

type TransformControlsGizmoRoot = {
  gizmo?: Partial<Record<"translate" | "rotate" | "scale", THREE.Object3D>>;
};

function getTransformControlsGizmoRoot(
  controls: TransformControlsImpl,
): TransformControlsGizmoRoot | undefined {
  return (controls as unknown as { gizmo?: TransformControlsGizmoRoot }).gizmo;
}

type TransformControlsRaycastInternals = {
  enabled?: boolean;
  visible?: boolean;
  object?: THREE.Object3D;
  camera?: THREE.Camera;
  domElement?: HTMLElement;
  mode?: "translate" | "rotate" | "scale";
  raycaster?: THREE.Raycaster;
  plane?: THREE.Object3D;
  gizmo?: {
    picker?: Partial<Record<"translate" | "rotate" | "scale", THREE.Object3D>>;
  };
};
